// src/Pages/Users.jsx
import React, {useEffect, useMemo, useState} from "react";
import Navigation from "../Components/Navigation";
import API from "../api/config.js";
import toast from "react-hot-toast";

const EyeIcon = () => (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor">
        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" strokeWidth="2" />
        <circle cx="12" cy="12" r="3" strokeWidth="2" />
    </svg>
);
const TrashIcon = () => (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor">
        <path
            d="M4 7h16M10 11v6m4-6v6M9 7V5h6v2m-8 0 1 12h8l1-12"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const statusOptions = ["All Status", "ACTIVE", "INACTIVE"];
const sortOptions = [
    {value: "items_desc", label: "Sort by Items"},
    {value: "items_asc", label: "Items (Low → High)"},
    {value: "name_asc", label: "Name (A → Z)"},
    {value: "joined_desc", label: "Newest First"},
    {value: "joined_asc", label: "Oldest First"},
];

const initialsOf = (full = "") =>
    full
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0]?.toUpperCase())
    .slice(0, 2)
    .join("");

const StatusPill = ({status}) => {
    const map =
        status === "ACTIVE"
            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
            : status === "INACTIVE"
            ? "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
            : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300";
    return <span className={`px-3 py-1 rounded-full text-xs font-medium ${map}`}>{status}</span>;
};

const ActionBtn = ({children, title, onClick}) => (
    <button
        type="button"
        title={title}
        onClick={onClick}
        className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
    >
        {children}
    </button>
);

const Users = () => {
    const [theme, setTheme] = useState(() => localStorage.getItem("travelxoxo-theme") || "light");
    const [activeScreen, setActiveScreen] = useState("users");

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [query, setQuery] = useState("");
    const [status, setStatus] = useState("All Status");
    const [sortBy, setSortBy] = useState("items_desc");
    const [page, setPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [selectedUserQrs, setSelectedUserQrs] = useState([]);

    const pageSize = 5;

    const handleViewUser = async (email) => {
        try {
            const res = await API.get("/registrations");
            const all = res.data?.registrations || [];
            const userQrs = all.filter((r) => r.email === email);

            setSelectedUserQrs(userQrs);
            setShowModal(true);
        } catch (e) {
            toast.error("Failed to load QR details");
        }
    };

    const handleDeleteSingleQr = async (qrId) => {
        if (!window.confirm(`Reset QR ${qrId}?`)) return;

        try {
            await API.delete(`/registrations/reset/${qrId}`);

            setSelectedUserQrs((prev) => prev.filter((qr) => qr.qr_id !== qrId));
        } catch {
            toast.error("Failed to reset QR");
        }
    };

    const handleDeleteUserQRCodes = async (email) => {
        if (!window.confirm("This will reset all QR codes for this user. Continue?")) return;

        try {
            const res = await API.get("/registrations");
            const all = res.data?.registrations || [];
            const userQrs = all.filter((r) => r.email === email);

            for (const qr of userQrs) {
                await API.delete(`/registrations/reset/${qr.qr_id}`);
            }

            setUsers((prev) => prev.filter((u) => u.email !== email));
        } catch {
            toast.error("Failed to reset QR codes");
        }
    };

    useEffect(() => {
        document.documentElement.classList.toggle("dark", theme === "dark");
        localStorage.setItem("travelxoxo-theme", theme);
    }, [theme]);
    const toggleTheme = () => setTheme((t) => (t === "light" ? "dark" : "light"));

    // Fetch registered users from /registrations (which reads from the users table)

    useEffect(() => {
        let isMounted = true;

        async function fetchUsers() {
            setLoading(true);
            setError(null);

            try {
                const res = await API.get("/registrations");
                const rows = res.data?.registrations || [];

                // AGGREGATE BY EMAIL
                const map = new Map();

                rows.forEach((u) => {
                    const key = u.email; // primary unique key

                    if (!map.has(key)) {
                        map.set(key, {
                            id: u.user_id ?? u.id,
                            name: u.owner_name ?? u.user_name,
                            email: u.email,

                            items: 1,
                            joined: u.date_registered ?? u.join_date ?? u.created_at,
                            status: (u.status || "").toUpperCase() === "ACTIVE" ? "ACTIVE" : "INACTIVE",
                        });
                    } else {
                        const existing = map.get(key);
                        existing.items += 1;

                        // keep earliest join date
                        const created = u.date_registered ?? u.join_date ?? u.created_at;
                        if (created && new Date(created) < new Date(existing.joined)) {
                            existing.joined = created;
                        }
                    }
                });

                const formatted = Array.from(map.values());
                if (isMounted) setUsers(formatted);
            } catch (e) {
                console.error("Failed to load users:", e?.response?.data || e.message);
                if (isMounted) setError("Failed to load users");
            } finally {
                if (isMounted) setLoading(false);
            }
        }

        fetchUsers();
        return () => {
            isMounted = false;
        };
    }, []);

    // Filter + sort
    const filtered = useMemo(() => {
        let list = [...users];
        const q = query.trim().toLowerCase();

        if (q) {
            list = list.filter((u) => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
        }

        if (status !== "All Status") {
            list = list.filter((u) => u.status === status);
        }

        list.sort((a, b) => {
            switch (sortBy) {
                case "items_asc":
                    return (a.items ?? 0) - (b.items ?? 0);
                case "items_desc":
                    return (b.items ?? 0) - (a.items ?? 0);
                case "name_asc":
                    return (a.name || "").localeCompare(b.name || "");
                case "joined_asc":
                    return new Date(a.joined || 0) - new Date(b.joined || 0);
                case "joined_desc":
                    return new Date(b.joined || 0) - new Date(a.joined || 0);
                default:
                    return 0;
            }
        });
        return list;
    }, [users, query, status, sortBy]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

    useEffect(() => {
        setPage(1);
    }, [query, status, sortBy]);

    // Export CSV for all filtered users (ignores pagination)
    const exportCSV = () => {
        if (!filtered.length) {
            toast.error("No data to export");
            return;
        }

        const headers = ["User ID", "Name", "Email", "url", "Join Date", "Status"];

        const rows = filtered.map((u) => [u.id, `"${u.name}"`, u.email, u.url, u.joined, u.status]);

        let csv = headers.join(",") + "\n";
        rows.forEach((r) => (csv += r.join(",") + "\n"));

        const blob = new Blob([csv], {type: "text/csv;charset=utf-8;"});
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "users_export.csv";
        link.click();

        URL.revokeObjectURL(url);
    };

    return (
        <div className="flex bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
            <Navigation
                activeScreen={activeScreen}
                setActiveScreen={setActiveScreen}
                theme={theme}
                toggleTheme={toggleTheme}
            />

            <main className="flex-1 ml-20 md:ml-64 p-6 md:p-10">
                <div className="mb-6 flex items-center justify-between">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">User Management</h1>

                    <button
                        onClick={exportCSV}
                        className="inline-flex items-center gap-2 rounded-xl bg-pink-600 hover:bg-pink-700 px-4 py-2.5 text-white shadow-sm hover:bg-orange-700"
                    >
                        Export CSV
                    </button>
                </div>

                {/* Controls (your exact design) */}
                <div className="grid gap-3 md:grid-cols-3 mb-5">
                    <div className="col-span-1">
                        <div className="relative">
                            <input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search users..."
                                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-800 placeholder-gray-400 focus:border-gray-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                            />
                            <span className="pointer-events-none absolute right-3 top-2.5 text-gray-400">⌘K</span>
                        </div>
                    </div>

                    <div>
                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="w-full appearance-none rounded-xl border border-gray-200 bg-white px-4 py-2.5 pr-10 text-gray-800 focus:border-gray-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                        >
                            {statusOptions.map((s) => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="w-full appearance-none rounded-xl border border-gray-200 bg-white px-4 py-2.5 pr-10 text-gray-800 focus:border-gray-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                        >
                            {sortOptions.map((so) => (
                                <option key={so.value} value={so.value}>
                                    {so.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left text-sm">
                            <thead className="bg-gray-50 text-gray-600 dark:bg-gray-900/40 dark:text-gray-300">
                                <tr>
                                    <th className="px-6 py-4 font-semibold">User</th>
                                    <th className="px-6 py-4 font-semibold">Email</th>
                                    <th className="px-6 py-4 font-semibold">Items</th>
                                    <th className="px-6 py-4 font-semibold">Join Date</th>
                                    <th className="px-6 py-4 font-semibold">Status</th>
                                    <th className="px-6 py-4 font-semibold">Options</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {loading && (
                                    <tr>
                                        <td
                                            colSpan={8}
                                            className="px-6 py-16 text-center text-gray-500 dark:text-gray-400"
                                        >
                                            Loading…
                                        </td>
                                    </tr>
                                )}

                                {!loading &&
                                    !error &&
                                    paged.map((u) => (
                                        <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                                                        <span className="text-sm font-bold">{initialsOf(u.name)}</span>
                                                    </div>
                                                    <div className="font-medium text-gray-900 dark:text-gray-100">
                                                        {u.name}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-gray-700 dark:text-gray-200">
                                                {u.email || "—"}
                                            </td>

                                            <td className="px-6 py-4 font-semibold text-gray-900 dark:text-gray-100">
                                                {u.items ?? 0}
                                            </td>
                                            <td className="px-6 py-4 text-gray-700 dark:text-gray-200">
                                                {u.joined
                                                    ? new Date(u.joined).toLocaleDateString("en-GB", {
                                                          month: "short",
                                                          day: "2-digit",
                                                          year: "numeric",
                                                      })
                                                    : "—"}
                                            </td>
                                            <td className="px-6 py-4">
                                                <StatusPill status={u.status || "INACTIVE"} />
                                            </td>

                                            <td className="px-5 py-3">
                                                <div className="flex items-center gap-2">
                                                    {/* View */}
                                                    <ActionBtn title="View" onClick={() => handleViewUser(u.email)}>
                                                        <EyeIcon />
                                                    </ActionBtn>

                                                    {/* Delete */}
                                                    <ActionBtn
                                                        title="Delete"
                                                        onClick={() => handleDeleteUserQRCodes(u.email)}
                                                    >
                                                        <TrashIcon />
                                                    </ActionBtn>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}

                                {!loading && error && (
                                    <tr>
                                        <td colSpan={8} className="px-6 py-16 text-center text-rose-500">
                                            {error}
                                        </td>
                                    </tr>
                                )}

                                {!loading && !error && paged.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={8}
                                            className="px-6 py-16 text-center text-gray-500 dark:text-gray-400"
                                        >
                                            No users found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-center gap-2 px-4 py-4">
                        <button
                            className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                        >
                            ←
                        </button>

                        {Array.from({length: totalPages}).map((_, i) => {
                            const n = i + 1;
                            const active = n === page;
                            return (
                                <button
                                    key={n}
                                    onClick={() => setPage(n)}
                                    className={`h-10 w-10 rounded-xl text-sm font-semibold ${
                                        active
                                            ? "bg-pink-600 hover:bg-pink-700 text-white"
                                            : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                                    }`}
                                >
                                    {n}
                                </button>
                            );
                        })}

                        <button
                            className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                        >
                            →
                        </button>
                    </div>
                </div>

                {showModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                        <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-800">
                            <div className="mb-4 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Registered QR Codes</h2>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="text-gray-500 hover:text-gray-900 dark:hover:text-white"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="max-h-[400px] overflow-y-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 text-gray-600 dark:bg-gray-900/40 dark:text-gray-300">
                                        <tr className="text-left">
                                            <th className="px-6 py-4 text-gray-700 dark:text-gray-200">QR ID</th>
                                            <th className="px-6 py-4 text-gray-700 dark:text-gray-200">URL</th>

                                            <th className="px-6 py-4 text-gray-700 dark:text-gray-200">Status</th>
                                            <th className="px-6 py-4 text-gray-700 dark:text-gray-200">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedUserQrs.map((qr) => (
                                            <tr key={qr.qr_id} className="border-b dark:border-gray-700">
                                                <td className="px-6 py-4 text-gray-700 dark:text-gray-200">
                                                    {qr.qr_id}
                                                </td>
                                                <td className="px-6 py-4 text-gray-700 dark:text-gray-200">{qr.url}</td>
                                                <td className="px-6 py-4 text-gray-700 dark:text-gray-200">
                                                    {qr.status}
                                                </td>
                                                <td className="py-6 px-4">
                                                    <button
                                                        onClick={() => handleDeleteSingleQr(qr.qr_id)}
                                                        className="rounded-lg bg-red-600 px-3 py-1 text-xs text-white hover:bg-red-700"
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}

                                        {selectedUserQrs.length === 0 && (
                                            <tr>
                                                <td colSpan="4" className="py-6 text-center text-gray-500">
                                                    No QR codes found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default Users;
